/** Fable Hero Forge effects.ts: 160ms history, outer 40% of the actual blade.
 * Bounded CPU ribbon geometry; no free-running clock or GPU ownership. */
import type {ElementInk,Vec3} from '../../shared';
import {PIGMENT_LINEAR as P} from '../shaders/pigments';
export const TRAIL_LIFE_MS=160,TRAIL_FLOATS=12,TRAIL_CAP=4*31*6;
const COLORS:Record<ElementInk,readonly number[]>={gold:P.goldLight,lapis:P.lapisPale,ink:P.ink,vermilion:P.vermilion,verdigris:P.verdigris,minium:P.minium,folium:P.folium};
interface Sample {base:Vec3;tip:Vec3;at:number;width:number;element:ElementInk}
export class BladeTrails {
  private paths=new Map<number,Sample[]>();
  clear(key?:number):void {if(key===undefined)this.paths.clear();else this.paths.delete(key);}
  get size():number{return this.paths.size;}
  sample(key:number,base:Vec3,tip:Vec3,o:{atMs:number;element?:ElementInk;width?:number}):void {
    if(![key,o.atMs,...base,...tip].every(Number.isFinite))return;
    let path=this.paths.get(key);
    if(!path){if(this.paths.size>=4)this.paths.delete(this.paths.keys().next().value!);this.paths.set(key,path=[]);}
    const prior=path.at(-1);
    if(prior&&(o.atMs<prior.at||o.atMs-prior.at>80||Math.hypot(...tip.map((v,i)=>v-prior.tip[i]))>2.5))path.length=0;
    const sample:Sample={base:[...base],tip:[...tip],at:o.atMs,width:Number.isFinite(o.width)?Math.max(.08,Math.min(1,o.width!)):.4,element:o.element??'gold'};
    if(path.length&&prior?.at===o.atMs)path[path.length-1]=sample;else path.push(sample);
    if(path.length>32)path.shift();
  }
  prune(now:number):void {
    for(const [key,path] of this.paths){while(path.length&&now-path[0].at>=TRAIL_LIFE_MS)path.shift();if(!path.length)this.paths.delete(key);}
  }
  write(out:Float32Array,now:number):number {
    this.prune(now);let count=0;
    const vertex=(p:Vec3,s:Sample,u:number)=>{
      if((count+1)*TRAIL_FLOATS>out.length)return;
      const age=Math.max(0,(now-s.at)/TRAIL_LIFE_MS),fade=Math.max(0,1-age),col=COLORS[s.element],i=count++*TRAIL_FLOATS;
      out.set([p[0],p[1],p[2],1,col[0],col[1],col[2],fade*fade*.82,u,age,0,0],i);
    };
    for(const path of this.paths.values())for(let i=1;i<path.length;i++){
      const a=path[i-1],b=path[i];if(b.at>now||(count+6)*TRAIL_FLOATS>out.length)continue;
      if(Math.hypot(...a.tip.map((v,k)=>v-b.tip[k]))<.00015)continue;
      const inner=(s:Sample)=>s.tip.map((v,k)=>v+(s.base[k]-v)*s.width) as Vec3;
      const ai=inner(a),bi=inner(b);
      vertex(ai,a,0);vertex(a.tip,a,1);vertex(b.tip,b,1);
      vertex(ai,a,0);vertex(b.tip,b,1);vertex(bi,b,0);
    }
    return count;
  }
}
